\<template>
  <div>
    <h1>Buku Besar Bank</h1>
    <p>Menampilkan semua transaksi yang terjadi di sistem.</p>

    <div class="controls-container">
      <div class="search-box">
        <input 
          type="text" 
          v-model="searchQuery" 
          @input="handleSearch"
          placeholder="Cari referensi, nama, atau no. rekening..." 
        />
      </div>
    </div>

    <div v-if="loading" class="loading-message">Memuat data...</div>
    <div v-if="error" class="error-message">{{ error }}</div>

    <div v-if="!loading && transactions.length === 0 && !error" class="no-data-message">
      Belum ada transaksi yang sesuai dengan pencarian Anda.
    </div>

    <table v-if="!loading && transactions.length > 0" class="ledger-table">
      <thead>
        <tr>
          <th>ID</th>
          <th>Tanggal</th>
          <th>Tipe</th>
          <th>Dari</th>
          <th>Ke</th>
          <th class="amount">Jumlah</th>
          <th>Deskripsi</th>
        </tr>
      </thead>
      <tbody>
  <tr v-for="tx in transactions" :key="tx.id">
    <td>{{ tx.id }}</td>
    <td>{{ formatDateTime(tx.timestamp) }}</td>
    <!-- GANTI BARIS DI BAWAH INI -->
    <td class="capitalize">{{ tx.transaction_code.name }}</td>
    <td>
      <span v-if="tx.source_account">
        {{ tx.source_account.owner.full_name }} <br>
        <small>({{ tx.source_account.account_number }})</small>
      </span>
      <span v-else class="system-actor">BANK</span>
    </td>
    <td>
      <span v-if="tx.destination_account && tx.destination_account.owner.role !== 'system'">
        {{ tx.destination_account.owner.full_name }} <br>
        <small>({{ tx.destination_account.account_number }})</small>
      </span>
      <span v-else class="system-actor">BANK</span>
    </td>
    <td class="amount">Rp {{ formatCurrency(tx.amount) }}</td>
    <td>{{ tx.description }}</td>
  </tr>
</tbody>    </table>

    <!-- Pagination Controls -->
    <div v-if="totalPages > 1" class="pagination-controls">
      <button :disabled="currentPage === 1" @click="changePage(currentPage - 1)">Sebelumnya</button>
      <span>Halaman {{ currentPage }} dari {{ totalPages }}</span>
      <button :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">Selanjutnya</button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import api from '@/services/api';
import { useToast } from 'vue-toastification';

// State Management
const transactions = ref([]);
const loading = ref(true);
const error = ref(null);
const toast = useToast();

const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalItems = ref(0);
let searchTimeout = null;

const totalPages = computed(() => {
  return Math.ceil(totalItems.value / itemsPerPage.value) || 1;
});

// Fungsi untuk mengambil data dari API
const fetchGeneralLedger = async () => {
  loading.value = true;
  error.value = null;
  try {
    const response = await api.getGeneralLedger(currentPage.value, itemsPerPage.value, searchQuery.value);
    // Asumsi response format: { total: number, data: array }
    transactions.value = response.data.data;
    totalItems.value = response.data.total;
  } catch (err) {
    console.error("Gagal memuat Buku Besar:", err);
    error.value = "Gagal memuat data Buku Besar. Pastikan Anda memiliki hak akses.";
    toast.error(error.value);
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    currentPage.value = 1;
    fetchGeneralLedger();
  }, 500); // debounce 500ms
};

const changePage = (page) => {
  if (page > 0 && page <= totalPages.value) {
    currentPage.value = page;
    fetchGeneralLedger();
  }
};

// Fungsi bantuan untuk formatting
const formatCurrency = (value) => {
    return parseFloat(value).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDateTime = (value) => {
    return new Date(value).toLocaleString('id-ID', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
};

// Lifecycle Hook
onMounted(() => {
  fetchGeneralLedger();
});
</script>

<style scoped>
  .controls-container {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1rem;
    margin-top: 1rem;
  }
  .search-box input {
    padding: 0.5rem;
    width: 300px;
    border: 1px solid #ccc;
    border-radius: 4px;
  }
  .pagination-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 2rem;
    gap: 1rem;
  }
  .pagination-controls button {
    padding: 0.5rem 1rem;
    border: 1px solid #007bff;
    background-color: white;
    color: #007bff;
    border-radius: 4px;
    cursor: pointer;
  }
  .pagination-controls button:disabled {
    border-color: #ccc;
    color: #ccc;
    cursor: not-allowed;
  }
  .pagination-controls button:not(:disabled):hover {
    background-color: #007bff;
    color: white;
  }
  /* Style untuk tabel, mirip dengan halaman Supervisor */
  .loading-message, .no-data-message, .error-message {
    text-align: center;
    padding: 2rem;
    color: #666;
  }
  .error-message {
    color: #dc3545;
  }
  .ledger-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
    font-size: 0.9rem;
  }
  .ledger-table th, .ledger-table td {
    border: 1px solid #ddd;
    padding: 0.75rem;
    text-align: left;
    vertical-align: top;
  }
  .ledger-table th {
    background-color: #f8f9fa;
  }
  .amount {
    text-align: right;
    font-weight: bold;
    white-space: nowrap;
  }
  .capitalize {
    text-transform: capitalize;
  }
  .system-actor {
    font-weight: bold;
    color: #007bff;
  }
  small {
    color: #6c757d;
  }
</style>